// Copyright (c) Ping. All rights reserved.
//
// Group-call grid — Figma Desktop "Call Participants" (2444:8051/8063): equal,
// fixed-size square participant cards, laid out in centered rows that stack
// vertically and centre as a block. Cards grow to fill the space (capped at
// 400, floored at a min) and the area auto-scrolls vertically once there are
// too many to fit. The column count is chosen to maximise card size while still
// fitting the viewport without scroll (so 7 → 4+3, 8 → 4+4, like Figma).

import { useState } from 'react';
import { type LayoutChangeEvent,ScrollView, View } from 'react-native';

import type { LiveKitParticipantView } from '@calls/livekit/LiveKitCallClient';

import { ParticipantTile } from './ParticipantTile';

const GAP = 12;
const MIN_CARD = 200;
const MAX_CARD = 400;

function bestLayout(
  n: number,
  w: number,
  h: number,
): { card: number; scroll: boolean } {
  if (n <= 0 || w <= 0 || h <= 0) return { card: MIN_CARD, scroll: false };
  let best = { card: 0, scroll: false };
  // Pick the column count that yields the largest square card that still fits
  // the area without scrolling (capped at MAX, floored at MIN).
  for (let cols = 1; cols <= n; cols += 1) {
    const rows = Math.ceil(n / cols);
    const cw = (w - (cols - 1) * GAP) / cols;
    const ch = (h - (rows - 1) * GAP) / rows;
    const card = Math.min(cw, ch, MAX_CARD);
    if (card < MIN_CARD) continue;
    if (card > best.card) best = { card, scroll: false };
  }
  if (best.card === 0) {
    // Nothing fits without scrolling — fill the width at the min size and scroll.
    const cols = Math.max(1, Math.min(n, Math.floor((w + GAP) / (MIN_CARD + GAP))));
    const card = Math.min(MAX_CARD, Math.max(MIN_CARD, (w - (cols - 1) * GAP) / cols));
    best = { card, scroll: true };
  }
  return best;
}

export function GroupGrid({
  participants,
  speakingSet,
  pinnedIdentity,
  onTogglePin,
  selfHand,
  selfAway,
  raisedHands,
  awayParticipants,
  videoIdentities,
}: {
  participants: LiveKitParticipantView[];
  speakingSet: Set<string>;
  pinnedIdentity: string | null;
  onTogglePin: (identity: string) => void;
  selfHand: boolean;
  selfAway: boolean;
  raisedHands: Record<string, boolean>;
  awayParticipants: Record<string, boolean>;
  /** Identities allowed to decode live video (decode budget). undefined → all. */
  videoIdentities?: ReadonlySet<string>;
}): JSX.Element {
  const [box, setBox] = useState({ w: 0, h: 0 });
  const onLayout = (e: LayoutChangeEvent) =>
    setBox({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height });

  const { card } = bestLayout(participants.length, box.w, box.h);

  return (
    <View className="flex-1" onLayout={onLayout}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
      >
        {/* Centered, wrapping rows of equal square cards. The last partial row
            centres automatically (justify-center + fixed card sizes). */}
        <View
          style={{ gap: GAP }}
          className="flex-row flex-wrap items-center justify-center"
        >
          {participants.map((p) => (
            <View key={p.identity} style={{ width: card, height: card }}>
              <ParticipantTile
                p={p}
                speaking={speakingSet.has(p.identity)}
                handRaised={p.isLocal ? selfHand : !!raisedHands[p.identity]}
                away={p.isLocal ? selfAway : !!awayParticipants[p.identity]}
                pinned={pinnedIdentity === p.identity}
                onTogglePin={onTogglePin}
                allowVideo={videoIdentities === undefined || videoIdentities.has(p.identity)}
              />
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}
